// Blob Storage Service for interacting with Azure Blob Storage API

const API_BASE_URL = `${process.env.REACT_APP_API_URI}/fileupload`;

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('auth_token');
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
};

export const blobStorageService = {
    /**
     * List all blobs in a container
     * @param {string} container - Container name (e.g., 'profilepics', 'documents', 'videos')
     * @returns {Promise<Array>} Array of blob objects
     */
    async listBlobs(container) {
        try {
            const response = await fetch(`${API_BASE_URL}/list?container=${container}`, {
                headers: authHeaders(),
            });
            
            if (!response.ok) {
                throw new Error(`Failed to list blobs: ${response.statusText}`);
            }
            
            const data = await response.json();
            return data.blobs || [];
        } catch (error) {
            console.error('Error listing blobs:', error);
            throw error;
        }
    },

    /**
     * Upload a profile picture
     * @param {File} file - The file to upload
     * @param {string} userId - The user ID
     * @returns {Promise<Object>} Upload result with URL
     */
    async uploadProfilePicture(file) {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE_URL}/profile-picture`, {
                method: 'POST',
                headers: authHeaders(),
                body: formData,
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Upload failed');
            }

            return await response.json();
        } catch (error) {
            console.error('Error uploading profile picture:', error);
            throw error;
        }
    },

    /**
     * Upload a document
     * @param {File} file - The file to upload
     * @param {string} userId - The user ID
     * @returns {Promise<Object>} Upload result with URL
     */
    async uploadDocument(file, userId) {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE_URL}/document`, {
                method: 'POST',
                headers: authHeaders(),
                body: formData,
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Upload failed');
            }

            return await response.json();
        } catch (error) {
            console.error('Error uploading document:', error);
            throw error;
        }
    },

    /**
     * Upload a video
     * @param {File} file - The file to upload
     * @returns {Promise<Object>} Upload result with URL
     */
    async uploadVideo(file) {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE_URL}/video`, {
                method: 'POST',
                headers: authHeaders(),
                body: formData,
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Upload failed');
            }

            return await response.json();
        } catch (error) {
            console.error('Error uploading video:', error);
            throw error;
        }
    },

    /**
     * Get a temporary SAS URL for a private blob
     * @param {string} container - Container name
     * @param {string} blobName - Blob name/path
     * @returns {Promise<string>} Temporary SAS URL
     */
    async getSasUrl(container, blobName) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/sas-url?container=${container}&blobName=${encodeURIComponent(blobName)}`,
                { headers: authHeaders() }
            );

            if (!response.ok) {
                throw new Error(`Failed to get SAS URL: ${response.statusText}`);
            }

            const data = await response.json();
            return data.url;
        } catch (error) {
            console.error('Error getting SAS URL:', error);
            throw error;
        }
    },

    /**
     * Delete a blob
     * @param {string} container - Container name
     * @param {string} blobName - Blob name/path
     * @returns {Promise<boolean>} True if deleted
     */
    async deleteBlob(container, blobName) {
        try {
            const response = await fetch(
                `${API_BASE_URL}?container=${container}&blobName=${encodeURIComponent(blobName)}`,
                { method: 'DELETE', headers: authHeaders() }
            );

            if (!response.ok) {
                throw new Error(`Failed to delete blob: ${response.statusText}`);
            }

            const data = await response.json();
            return data.success;
        } catch (error) {
            console.error('Error deleting blob:', error);
            throw error;
        }
    }
};
